import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  Stack,
  Flex,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Heading,
  useColorModeValue,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiTrendingUp, FiUsers, FiDollarSign, FiBox, FiActivity, FiMapPin, FiUserPlus, FiPlus } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { getBackendURL, apiFetch, getDateInFormat } from '../../utils/utils';

const MotionBox = motion(Box);

const StatCard = ({ title, stat, helpText, icon, color }) => {
  const bg = useColorModeValue('white', 'gray.700');
  return (
    <MotionBox
      p={5}
      shadow="md"
      borderWidth="1px"
      borderRadius="xl"
      bg={bg}
      whileHover={{ y: -4, shadow: 'lg' }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <Stat>
            <StatLabel fontWeight="medium" color="gray.500">
              {title}
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold">
              {stat}
            </StatNumber>
            {helpText && (
              <StatHelpText mb={0}>
                <StatArrow type="increase" />
                {helpText}
              </StatHelpText>
            )}
          </Stat>
        </Box>
        <Flex
          w={12}
          h={12}
          bg={`${color}.50`}
          color={`${color}.500`}
          rounded="full"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={icon} w={6} h={6} />
        </Flex>
      </Flex>
    </MotionBox>
  );
};

function HomePage() {
  const user = useSelector((state) => state.userState.user);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const itemBg = useColorModeValue('gray.50', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  useLayoutEffect(() => {
    if (!user) {
      window.location = '/';
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    apiFetch(`${getBackendURL()}/admin/dashboard/stats`)
      .then((res) => res.json())
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching dashboard stats:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Flex direction="column" align="center" justify="center" minH="50vh" w="full">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.500">
          Gathering platform insights...
        </Text>
      </Flex>
    );
  }

  return (
    <Box p={4} maxW="container.xl" mx="auto">
      <Breadcrumb mb={6}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/" fontSize="sm" color="gray.500">
            Home
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink fontSize="sm" color="blue.500" fontWeight="semibold">
            Dashboard
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Heading size="lg" mb={8} fontWeight="bold">
        Platform Overview
      </Heading>

      {/* KPI Stats Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={10}>
        <StatCard
          title="Total Platform Revenue"
          stat={`₹${stats?.revenue?.platformTotal?.toLocaleString()}`}
          helpText="Overall direct income"
          icon={FiDollarSign}
          color="green"
        />
        <StatCard
          title="Direct Merchants"
          stat={stats?.merchants?.total}
          helpText={`${stats?.merchants?.active} active currently`}
          icon={FiUsers}
          color="blue"
        />
        <StatCard
          title="Active Distributors"
          stat={stats?.distributors?.active}
          helpText={`Out of ${stats?.distributors?.total} partners`}
          icon={FiBox}
          color="purple"
        />
        <StatCard
          title="Published Digi-Cards"
          stat={stats?.sites?.totalDirect}
          helpText="Directly managed sites"
          icon={FiTrendingUp}
          color="orange"
        />
      </SimpleGrid>

      {/* Activity Sections */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
        {/* Recent Payments */}
        <Box
          bg={cardBg}
          p={6}
          rounded="xl"
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" mb={6}>
            <Heading size="sm" display="flex" alignItems="center">
              <Icon as={FiActivity} mr={2} color="green.500" />
              Latest Direct Payments
            </Heading>
            <Badge colorScheme="green" rounded="full" px={2}>
              New
            </Badge>
          </Flex>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Merchant</Th>
                  <Th>Plan</Th>
                  <Th isNumeric>Amount</Th>
                  <Th>Date</Th>
                </Tr>
              </Thead>
              <Tbody>
                {stats?.recentActivity?.payments?.map((payment, idx) => (
                  <Tr key={payment._id || idx}>
                    <Td fontWeight="medium">{payment.displayName || payment.email}</Td>
                    <Td>{payment.planName}</Td>
                    <Td isNumeric fontWeight="bold" color="green.600">
                      ₹{payment.amount}
                    </Td>
                    <Td color="gray.500" fontSize="xs">
                      {getDateInFormat(payment.txnDate)}
                    </Td>
                  </Tr>
                ))}
                {(!stats?.recentActivity?.payments || stats.recentActivity.payments.length === 0) && (
                  <Tr>
                    <Td colSpan={4} textAlign="center" py={4} color="gray.400">
                      No recent payments found
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* New Distributors */}
        <Box
          bg={cardBg}
          p={6}
          rounded="xl"
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" mb={6}>
            <Heading size="sm" display="flex" alignItems="center">
              <Icon as={FiMapPin} mr={2} color="purple.500" />
              Newest Distributors
            </Heading>
            <Link to="/distributor-chain">
              <Text fontSize="xs" color="blue.500" fontWeight="bold">
                VIEW ALL
              </Text>
            </Link>
          </Flex>
          <Stack spacing={4}>
            {stats?.recentActivity?.distributors?.map((dist, idx) => (
              <Flex
                key={dist._id || idx}
                p={3}
                rounded="lg"
                bg={itemBg}
                align="center"
              >
                <Box
                  w={10}
                  h={10}
                  bg="purple.100"
                  color="purple.500"
                  rounded="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mr={4}
                >
                  <Text fontWeight="bold">{dist.displayName?.charAt(0) || 'D'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm">
                    {dist.displayName || dist.username}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {dist.email}
                  </Text>
                </Box>
                <Badge ml="auto" colorScheme={dist.isActive ? 'green' : 'red'}>
                  {dist.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Flex>
            ))}
            {(!stats?.recentActivity?.distributors || stats.recentActivity.distributors.length === 0) && (
              <Text textAlign="center" color="gray.400">
                No distributors yet
              </Text>
            )}
          </Stack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HomePage;